import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { SOCIALPULSE_CLIENT_ID, socialpulseRedirectUri } from "@/lib/socialpulse";
import { toast } from "sonner";
import { GraduationCap, CircleNotch, WarningCircle } from "@phosphor-icons/react";

export default function SocialPulseCallback() {
  const { socialpulseLogin } = useAuth();
  const nav = useNavigate();
  const [error, setError] = useState("");

  useEffect(() => {
    (async () => {
      const params = new URLSearchParams(window.location.search);
      const code = params.get("code");
      const returnedState = params.get("state");
      const expectedState = sessionStorage.getItem("sp_state");
      const verifier = sessionStorage.getItem("sp_verifier");
      const nonce = sessionStorage.getItem("sp_nonce");
      const keep = sessionStorage.getItem("sp_keep") !== "0";

      // One-time use — clear immediately.
      sessionStorage.removeItem("sp_state");
      sessionStorage.removeItem("sp_verifier");
      sessionStorage.removeItem("sp_nonce");
      sessionStorage.removeItem("sp_keep");

      if (params.get("error")) throw new Error(params.get("error_description") || params.get("error"));
      if (!code || !returnedState || returnedState !== expectedState || !verifier) {
        throw new Error("This sign-in link is invalid or has expired. Please try again.");
      }

      const res = await socialpulseLogin({
        code,
        code_verifier: verifier,
        redirect_uri: socialpulseRedirectUri(),
        client_id: SOCIALPULSE_CLIENT_ID,
        nonce,
        trust_device: keep,
      });
      toast.success(res.is_new_user ? "Welcome to AlumniPulse!" : "Welcome back!");
      nav("/dashboard", { replace: true });
    })().catch((e) => setError(e?.response?.data?.detail || e.message || "Sign-in failed"));
  }, [socialpulseLogin, nav]);

  return (
    <div className="grid min-h-screen place-items-center bg-background px-6" data-testid="socialpulse-callback">
      <div className="w-full max-w-sm text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-primary text-primary-foreground">
          <GraduationCap weight="fill" size={24} />
        </span>
        {!error ? (
          <>
            <CircleNotch size={26} className="mx-auto mt-6 animate-spin text-secondary" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="socialpulse-status">Signing you in with SocialPulse…</p>
          </>
        ) : (
          <>
            <WarningCircle size={26} className="mx-auto mt-6 text-destructive" />
            <p className="mt-4 text-sm text-destructive" data-testid="socialpulse-error">{error}</p>
            <button onClick={() => nav("/login", { replace: true })} className="mt-6 rounded-full bg-primary px-5 py-2 text-sm text-primary-foreground" data-testid="socialpulse-back-login">
              Back to sign in
            </button>
          </>
        )}
      </div>
    </div>
  );
}
